import React, { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { GlobalEnquiryContext } from '../App';
import HomeNavBar from '../Component/HomeComponent/HomeNavBar';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    MapPin, 
    ArrowLeft, 
    CheckCircle2, 
    ChevronDown, 
    Briefcase, 
    AlertCircle, 
    Settings, 
    Target, 
    TrendingUp, 
    Layers, 
    Building2, 
    HelpCircle,
    ArrowRight,
    Sparkles
} from 'lucide-react';
import { locationsData } from '../Component/LocationsData';
import buildingImg from '../Assest/cards/building.avif';

const LocationPage = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { openEnquiry } = useContext(GlobalEnquiryContext);
    
    const [content, setContent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [openFaq, setOpenFaq] = useState(null);

    useEffect(() => {
        window.scrollTo(0, 0);
        if (slug) {
            const normalized = slug.toLowerCase();
            const data = locationsData[normalized];
            if (data) {
                setContent(data);
            }
        }
        setLoading(false);
    }, [slug]);

    if (loading) {
        return (
            <div className="bg-[#01014C] min-h-screen flex items-center justify-center text-white">
                <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    if (!content) {
        return (
            <div className="bg-[#01014C] min-h-screen flex flex-col items-center justify-center text-white p-6">
                <h2 className="text-3xl font-bold mb-4">Location Page Not Found</h2>
                <button onClick={() => navigate('/')} className="px-6 py-3 bg-blue-600 hover:bg-white hover:text-blue-600 border border-blue-600 text-white rounded-xl transition-all duration-300">Return Home</button>
            </div>
        );
    }

    const toggleFaq = (idx) => {
        setOpenFaq(openFaq === idx ? null : idx);
    };

    return (
        <div className="bg-slate-50 text-slate-800 min-h-screen font-sans">
            <Helmet>
                <title>{content.meta_title}</title>
                <meta name="description" content={content.meta_description} />
                {((content.secondary_keywords && content.secondary_keywords.length > 0) || 
                  (content.long_tail_keywords && content.long_tail_keywords.length > 0)) && (
                    <meta 
                        name="keywords" 
                        content={[
                            ...(content.secondary_keywords || []),
                            ...(content.long_tail_keywords || [])
                        ].join(', ')} 
                    />
                )}
                <link rel="canonical" href={`https://www.meridahr.com/locations/${content.slug}`} />
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "RecruitmentAgency",
                        "name": `Merida HR Consulting - ${content.city}`,
                        "image": "https://www.meridahr.com/MeridaConsultancyLogo.png",
                        "telephone": "+91-8904599282",
                        "email": "info@meridahr.com",
                        "url": `https://www.meridahr.com/locations/${content.slug}`,
                        "address": {
                            "@type": "PostalAddress",
                            "addressLocality": content.city,
                            "addressRegion": content.city === "Delhi-NCR" ? "Delhi" : (content.city === "Bangalore" ? "Karnataka" : (content.city === "Chennai" ? "Tamil Nadu" : (content.city === "Hyderabad" ? "Telangana" : "Maharashtra"))),
                            "addressCountry": "IN"
                        },
                        "priceRange": "$$",
                        "aggregateRating": {
                            "@type": "AggregateRating",
                            "ratingValue": "4.8",
                            "reviewCount": "85"
                        }
                    })}
                </script>
                {content.faqs && content.faqs.list && content.faqs.list.length > 0 && (
                    <script type="application/ld+json">
                        {JSON.stringify({
                            "@context": "https://schema.org",
                            "@type": "FAQPage",
                            "mainEntity": content.faqs.list.map(f => ({
                                "@type": "Question",
                                "name": f.q,
                                "acceptedAnswer": {
                                    "@type": "Answer",
                                    "text": f.a
                                }
                            }))
                        })}
                    </script>
                )}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "BreadcrumbList",
                        "itemListElement": [
                            {
                                "@type": "ListItem",
                                "position": 1,
                                "name": "Home",
                                "item": "https://www.meridahr.com/"
                            },
                            {
                                "@type": "ListItem",
                                "position": 2,
                                "name": "Locations",
                                "item": "https://www.meridahr.com/"
                            },
                            {
                                "@type": "ListItem",
                                "position": 3,
                                "name": content.city,
                                "item": `https://www.meridahr.com/locations/${content.slug}`
                            }
                        ]
                    })}
                </script>
            </Helmet>

            <div className="absolute w-full top-0 left-0 z-50">
                <HomeNavBar type="white" />
            </div>

            {/* HERO SECTION */}
            <section className="relative pt-36 pb-24 overflow-hidden bg-[#01014C] text-white">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(37,99,235,0.18),transparent_60%)] pointer-events-none" />
                <div className="container mx-auto px-6 relative z-10">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                        <div className="lg:col-span-7 text-left space-y-6">
                            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-500/20 text-blue-300 text-xs font-bold uppercase tracking-wider border border-blue-500/30">
                                <MapPin size={12} /> Merida {content.city} Hub
                            </span>
                            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold syne leading-tight text-white">
                                {content.hero.h1}
                            </h1>
                            <p className="text-blue-100/70 text-base md:text-lg leading-relaxed font-light max-w-2xl">
                                {content.hero.description}
                            </p>
                            <div className="flex flex-wrap gap-4 pt-2">
                                <button 
                                    onClick={() => openEnquiry(`Location ${content.city} Consultation`)}
                                    className="px-8 py-4 bg-blue-600 hover:bg-white hover:text-[#01014C] text-white font-bold uppercase tracking-wider text-xs rounded-xl transition-all duration-300 shadow-xl"
                                >
                                    {content.hero.primary_cta}
                                </button>
                                <a 
                                    href="#solutions-section"
                                    className="px-8 py-4 bg-white/5 hover:bg-white/10 border border-white/10 text-white font-bold uppercase tracking-wider text-xs rounded-xl transition-all duration-300 flex items-center justify-center"
                                >
                                    {content.hero.secondary_cta}
                                </a>
                            </div>
                        </div>
                        <div className="lg:col-span-5 relative hidden lg:block">
                            <div className="absolute -inset-1.5 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-[2.5rem] blur opacity-25" />
                            <div className="relative overflow-hidden rounded-[2.5rem] border border-white/10 shadow-2xl bg-gradient-to-br from-blue-950/80 to-[#01014C]/80 aspect-[4/3]">
                                <img 
                                    src={buildingImg} 
                                    alt={`Merida HR ${content.city}`} 
                                    className="w-full h-full object-cover opacity-90"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* OVERVIEW SECTION */}
            <section className="py-24 bg-white">
                <div className="container mx-auto px-6 max-w-5xl">
                    <div className="text-center mb-16 max-w-3xl mx-auto">
                        <span className="text-blue-600 font-bold tracking-[0.3em] text-[10px] uppercase block mb-3">Ecosystem Overview</span>
                        <h2 className="text-3xl md:text-5xl font-bold syne text-[#01014C] mb-6 leading-tight">
                            {content.overview.h2}
                        </h2>
                        <p className="text-slate-600 text-sm md:text-base leading-relaxed">
                            {content.overview.desc}
                        </p>
                    </div>

                    {content.overview.bullets && content.overview.bullets.length > 0 && (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
                            {content.overview.bullets.map((bullet, idx) => (
                                <div key={idx} className="flex items-center gap-3 bg-slate-50 border border-slate-200/50 p-4 rounded-2xl hover:border-blue-500/20 hover:shadow-md transition-all">
                                    <div className="w-2 h-2 rounded-full bg-blue-600" />
                                    <span className="text-sm font-semibold text-slate-700">{bullet}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* ENQUIRY FORM SECTION */}
            <section className="py-24 bg-slate-50 border-t border-slate-200/50">
                <div className="container mx-auto px-6 max-w-5xl">
                    <div className="bg-white border border-slate-200/60 rounded-[3rem] p-8 md:p-14 shadow-xl grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                        <div className="lg:col-span-5 space-y-6 text-left">
                            <span className="text-blue-600 font-bold tracking-[0.3em] text-[10px] uppercase block">Regional Consultation</span>
                            <h2 className="text-3xl font-bold text-[#01014C] syne leading-tight">
                                {content.enquiry.h2}
                            </h2>
                            <p className="text-slate-500 text-sm leading-relaxed">
                                {content.enquiry.description}
                            </p>
                        </div>
                        <div className="lg:col-span-7 bg-slate-50 p-8 md:p-12 rounded-[2rem] border border-slate-100 flex flex-col justify-center items-start space-y-6 text-left shadow-inner">
                            <h3 className="text-xl font-bold text-[#01014C] syne">Why Connect with Our {content.city} Team?</h3>
                            <div className="space-y-4 w-full">
                                <div className="flex items-start gap-3">
                                    <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center shrink-0 text-xs font-bold">1</div>
                                    <p className="text-slate-600 text-sm"><strong className="text-slate-800">Localized Talent Pools:</strong> Access to {content.city}'s top-tier active candidate networks across tech and non-tech sectors.</p>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center shrink-0 text-xs font-bold">2</div>
                                    <p className="text-slate-600 text-sm"><strong className="text-slate-800">End-to-End Compliance:</strong> Complete management of payroll, statutory benefits, and local labour compliance regulations.</p>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center shrink-0 text-xs font-bold">3</div>
                                    <p className="text-slate-600 text-sm"><strong className="text-slate-800">Accelerated Timelines:</strong> Direct sourcing pipelines reducing recruitment cycles by up to 57%.</p>
                                </div>
                            </div>
                            <button
                                onClick={() => openEnquiry(`Location Page - ${content.city}`, content.enquiry.fields)}
                                className="mt-4 px-8 py-4 bg-blue-600 border border-blue-600 hover:bg-white hover:text-blue-600 text-white font-bold uppercase tracking-wider text-xs rounded-xl transition-all duration-300 shadow-lg shadow-blue-200 w-full sm:w-auto text-center"
                            >
                                {content.enquiry.cta}
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* CHALLENGES SECTION */}
            {content.challenges.list && content.challenges.list.length > 0 && (
                <section className="py-24 bg-white">
                    <div className="container mx-auto px-6 max-w-5xl">
                        <div className="text-center max-w-3xl mx-auto mb-16">
                            <span className="text-blue-600 font-bold tracking-[0.3em] text-[10px] uppercase block mb-3">Workforce Bottlenecks</span>
                            <h2 className="text-3xl md:text-5xl font-bold syne text-[#01014C] mb-4 leading-tight">
                                {content.challenges.h2}
                            </h2>
                            <p className="text-slate-500 text-sm md:text-base leading-relaxed">
                                Key staffing bottlenecks, talent matching hurdles, and compliance complexities faced by regional businesses.
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
                            {content.challenges.list.map((challenge, idx) => (
                                <div key={idx} className="p-8 bg-slate-50 border border-slate-200/60 rounded-[2.5rem] hover:bg-white hover:border-blue-500/20 hover:shadow-xl transition-all duration-300 flex flex-col justify-between text-left shadow-sm">
                                    <div>
                                        <div className="w-12 h-12 bg-blue-50 text-blue-600 flex items-center justify-center rounded-2xl mb-6 font-bold text-lg">
                                            0{idx + 1}
                                        </div>
                                        <h3 className="text-lg font-bold text-[#01014C] mb-4 syne">{challenge.h3}</h3>
                                        <p className="text-slate-500 text-xs font-semibold mb-4">{challenge.description}</p>
                                        
                                        {challenge.bullets && challenge.bullets.length > 0 && (
                                            <ul className="space-y-2 mb-6">
                                                {challenge.bullets.map((b, bIdx) => (
                                                    <li key={bIdx} className="flex items-center gap-2 text-slate-600 text-sm">
                                                        <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                                                        {b}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                    {challenge.conclusion && (
                                        <p className="text-xs font-bold text-blue-600 border-t border-slate-200/50 pt-4 mt-2">
                                            {challenge.conclusion}
                                        </p>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* SOLUTIONS SECTION */}
            <id id="solutions-section">
                <section className="py-24 bg-[#01014C] text-white">
                    <div className="container mx-auto px-6 max-w-5xl">
                        <div className="text-center max-w-3xl mx-auto mb-16">
                            <span className="text-blue-400 font-bold tracking-[0.3em] text-[10px] uppercase block mb-3">Strategic Workforce Planning</span>
                            <h2 className="text-3xl md:text-5xl font-bold syne text-white mb-4 leading-tight">
                                {content.solutions.h2}
                            </h2>
                            <p className="text-blue-100/70 text-sm md:text-base leading-relaxed">
                                {content.solutions.intro}
                            </p>
                        </div>

                        {content.solutions.bullets && content.solutions.bullets.length > 0 && (
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-16 max-w-4xl mx-auto">
                                {content.solutions.bullets.map((sol, idx) => (
                                    <div key={idx} className="flex items-center gap-3 bg-white/5 border border-white/10 p-4 rounded-xl">
                                        <CheckCircle2 size={16} className="text-blue-400 shrink-0" />
                                        <span className="text-sm font-semibold text-blue-50">{sol}</span>
                                    </div>
                                ))}
                            </div>
                        )}

                        {content.solutions.conclusion && (
                            <p className="text-center text-blue-200/80 text-sm leading-relaxed max-w-2xl mx-auto italic">
                                {content.solutions.conclusion}
                            </p>
                        )}
                    </div>
                </section>
            </id>

            {/* SERVICES SECTION */}
            {content.services.list && content.services.list.length > 0 && (
                <section className="py-24 bg-white">
                    <div className="container mx-auto px-6 max-w-5xl">
                        <div className="text-center max-w-3xl mx-auto mb-16">
                            <span className="text-blue-600 font-bold tracking-[0.3em] text-[10px] uppercase block mb-3">Service Catalog</span>
                            <h2 className="text-3xl md:text-5xl font-bold syne text-[#01014C] mb-4 leading-tight">
                                {content.services.h2}
                            </h2>
                            <p className="text-slate-500 text-sm md:text-base leading-relaxed">
                                Deploying integrated workforce support systems across key corporate functions.
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {content.services.list.map((srv, idx) => (
                                <div key={idx} className="p-8 bg-slate-50 border border-slate-200/50 rounded-3xl hover:bg-white hover:shadow-xl hover:border-blue-500/20 transition-all duration-300">
                                    <h3 className="text-lg font-bold text-[#01014C] mb-3 syne">{srv.title}</h3>
                                    <p className="text-slate-500 text-sm leading-relaxed">{srv.desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* WHY CHOOSE SECTION */}
            <section className="py-24 bg-slate-50 border-t border-b border-slate-200/50">
                <div className="container mx-auto px-6 max-w-5xl">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                        <div className="lg:col-span-7 space-y-6 text-left">
                            <span className="text-blue-600 font-bold tracking-[0.3em] text-[10px] uppercase block">Why Choose Merida</span>
                            <h2 className="text-3xl md:text-5xl font-bold syne text-[#01014C] leading-tight">
                                {content.why_choose.h2}
                            </h2>
                            
                            {content.why_choose.paragraphs.map((p, idx) => (
                                <p key={idx} className="text-slate-600 text-sm md:text-base leading-relaxed">
                                    {p}
                                </p>
                            ))}

                            <div className="pt-4 border-t border-slate-200">
                                <h4 className="text-sm font-bold text-slate-800 uppercase tracking-wider mb-4">{content.why_choose.bullets_title}</h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {content.why_choose.bullets.map((b, idx) => (
                                        <div key={idx} className="flex items-center gap-2.5 text-sm text-slate-700">
                                            <CheckCircle2 size={16} className="text-emerald-500 shrink-0" />
                                            <span>{b}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="lg:col-span-5 bg-white p-8 rounded-[2.5rem] border border-slate-200/60 shadow-lg text-left space-y-6">
                            <div className="w-12 h-12 bg-blue-50 text-blue-600 flex items-center justify-center rounded-2xl">
                                <Layers size={24} />
                            </div>
                            <h3 className="text-xl font-bold text-[#01014C] syne">The Merida Alternative</h3>
                            <p className="text-slate-500 text-sm leading-relaxed">
                                {content.why_choose.conclusion}
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* METRICS SECTION */}
            {content.metrics.list && content.metrics.list.length > 0 && (
                <section className="py-24 bg-[#01014C] text-white relative">
                    <div className="container mx-auto px-6 max-w-5xl">
                        <div className="text-center max-w-3xl mx-auto mb-16">
                            <span className="text-blue-400 font-bold tracking-[0.3em] text-[10px] uppercase block mb-3">{content.metrics.title}</span>
                            <h2 className="text-3xl md:text-5xl font-bold syne text-white mb-4 leading-tight">
                                Measurable Workforce Execution
                            </h2>
                            <p className="text-blue-100/70 text-sm md:text-base leading-relaxed">
                                Key performance outcomes and indicators that define our regional service operations.
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {content.metrics.list.map((m, idx) => (
                                <div key={idx} className="p-8 bg-white/5 border border-white/10 rounded-[2rem] hover:bg-white/10 transition-all duration-300">
                                    <span className="text-blue-400 font-black text-2xl md:text-3xl block mb-2">{m.value}</span>
                                    <h4 className="text-white font-bold text-sm leading-tight mb-2">{m.label}</h4>
                                    <p className="text-blue-100/60 text-xs leading-relaxed">{m.desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* INDUSTRIES SECTION */}
            {content.industries.list && content.industries.list.length > 0 && (
                <section className="py-24 bg-white">
                    <div className="container mx-auto px-6 max-w-5xl">
                        <div className="text-center max-w-3xl mx-auto mb-16">
                            <span className="text-blue-600 font-bold tracking-[0.3em] text-[10px] uppercase block mb-3">Industry Alignment</span>
                            <h2 className="text-3xl md:text-5xl font-bold syne text-[#01014C] mb-4 leading-tight">
                                {content.industries.h2}
                            </h2>
                            <p className="text-slate-500 text-sm md:text-base leading-relaxed">
                                Delivering specialized workforce solutions across key vertical markets.
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                            {content.industries.list.map((ind, idx) => (
                                <div key={idx} className="p-6 bg-slate-50 border border-slate-200/50 rounded-2xl hover:bg-blue-50/35 transition-colors">
                                    <h4 className="text-base font-bold text-[#01014C] mb-2 syne">{ind.title}</h4>
                                    <p className="text-slate-500 text-xs leading-relaxed">{ind.desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* PROCESS SECTION */}
            {content.process.steps && content.process.steps.length > 0 && (
                <section className="py-24 bg-slate-50 border-t border-b border-slate-200/50">
                    <div className="container mx-auto px-6 max-w-5xl">
                        <div className="text-center max-w-3xl mx-auto mb-20">
                            <span className="text-blue-600 font-bold tracking-[0.3em] text-[10px] uppercase block mb-3">Operational Milestones</span>
                            <h2 className="text-3xl md:text-5xl font-bold syne text-[#01014C] mb-4 leading-tight">
                                {content.process.h2}
                            </h2>
                            <p className="text-slate-500 text-sm md:text-base leading-relaxed">
                                A timeline-driven, compliance-focused recruitment process customized for regional hiring parameters.
                            </p>
                        </div>

                        <div className="relative max-w-3xl mx-auto">
                            <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-0.5 bg-blue-200 -translate-x-1/2" />
                            {content.process.steps.map((step, idx) => (
                                <div key={idx} className={`flex items-center mb-12 w-full ${idx % 2 === 0 ? 'md:flex-row-reverse' : ''}`}>
                                    <div className="md:w-1/2" />
                                    <div className="absolute left-6 md:left-1/2 w-8 h-8 bg-blue-600 rounded-full border-4 border-white z-10 -translate-x-1/2 flex items-center justify-center text-[10px] text-white font-bold">{idx + 1}</div>
                                    <motion.div initial={{ opacity: 0, x: idx % 2 === 0 ? -30 : 30 }} whileInView={{ opacity: 1, x: 0 }} className="md:w-1/2 pl-14 md:px-12">
                                        <div className="bg-white border border-slate-200/60 p-6 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
                                            <h4 className="font-bold text-blue-600 uppercase text-[10px] tracking-widest mb-1">{step.step}</h4>
                                            <h3 className="font-bold text-[#01014C] mb-2 text-sm md:text-base">{step.title}</h3>
                                            <p className="text-slate-500 text-xs leading-relaxed">{step.desc}</p>
                                        </div>
                                    </motion.div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* BUSINESSES WE SUPPORT SECTION */}
            {content.businesses.list && content.businesses.list.length > 0 && (
                <section className="py-24 bg-white">
                    <div className="container mx-auto px-6 max-w-5xl">
                        <div className="text-center max-w-3xl mx-auto mb-16">
                            <span className="text-blue-600 font-bold tracking-[0.3em] text-[10px] uppercase block mb-3">Client Profiles</span>
                            <h2 className="text-3xl md:text-5xl font-bold syne text-[#01014C] mb-4 leading-tight">
                                {content.businesses.h2}
                            </h2>
                            <p className="text-slate-500 text-sm md:text-base leading-relaxed">
                                Supporting businesses of various sizes and operational structures across the region.
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {content.businesses.list.map((bus, idx) => (
                                <div key={idx} className="p-8 bg-slate-50 border border-slate-200/50 rounded-2xl hover:bg-white hover:shadow-xl hover:border-blue-500/20 transition-all duration-300">
                                    <h3 className="text-base font-bold text-[#01014C] mb-2 syne">{bus.title}</h3>
                                    <p className="text-slate-500 text-xs leading-relaxed">{bus.desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* FAQ SECTION */}
            {content.faqs.list && content.faqs.list.length > 0 && (
                <section className="py-24 bg-slate-50 border-t border-slate-200/50">
                    <div className="container mx-auto px-6 max-w-4xl">
                        <div className="text-center mb-16">
                            <span className="text-blue-600 font-bold tracking-[0.3em] text-[10px] uppercase block mb-3">Information Hub</span>
                            <h2 className="text-3xl md:text-5xl font-bold text-[#01014C] syne leading-tight mb-4">
                                {content.faqs.h2}
                            </h2>
                            <p className="text-slate-500 text-sm md:text-base leading-relaxed">
                                Clear explanations of our recruitment timelines, pricing models, payroll transitions, and compliance integration processes.
                            </p>
                        </div>

                        <div className="space-y-4">
                            {content.faqs.list.map((faq, idx) => {
                                const isOpen = openFaq === idx;
                                return (
                                    <div key={idx} className="border border-slate-200 rounded-2xl overflow-hidden bg-white transition-all duration-300">
                                        <button
                                            className="w-full flex justify-between items-center px-5 py-3.5 bg-slate-50 hover:bg-slate-100/50 text-left transition-colors"
                                            onClick={() => toggleFaq(idx)}
                                        >
                                            <span className="font-bold text-[#01014c] text-sm md:text-base pr-4">{faq.q}</span>
                                            <motion.div
                                                animate={{ rotate: isOpen ? 180 : 0 }}
                                                transition={{ duration: 0.3 }}
                                                className="shrink-0 text-blue-500"
                                            >
                                                <ChevronDown size={20} />
                                            </motion.div>
                                        </button>

                                        <AnimatePresence initial={false}>
                                            {isOpen && (
                                                <motion.div
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: "auto", opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                    transition={{ duration: 0.3 }}
                                                >
                                                    <div className="px-5 py-3.5 border-t border-slate-100 text-slate-600 text-sm leading-relaxed font-sans bg-white">
                                                        {faq.a}
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </section>
            )}

            {/* FINAL CTA */}
            <section className="py-24 bg-[#01014C] text-white text-center relative overflow-hidden">
                <div className="container mx-auto px-6 relative z-10 max-w-4xl">
                    <span className="inline-block bg-blue-500/20 text-blue-300 font-bold tracking-[0.2em] text-xs uppercase px-4 py-2 rounded-full mb-6 border border-blue-500/20">
                        Get Started
                    </span>
                    <h2 className="text-3xl md:text-5xl font-bold syne leading-tight mb-6">
                        {content.final_cta.h2}
                    </h2>
                    <p className="text-slate-300 text-base md:text-lg mb-10 max-w-2xl mx-auto leading-relaxed">
                        {content.final_cta.desc}
                    </p>

                    <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
                        <button 
                            onClick={() => openEnquiry(`Location ${content.city} Final Call`)}
                            className="px-8 py-3.5 bg-blue-600 hover:bg-white hover:text-[#01014C] text-white font-bold uppercase tracking-wider text-xs rounded-full transition-all duration-300"
                        >
                            {content.final_cta.primary}
                        </button>
                        <button
                            onClick={() => openEnquiry(`Location ${content.city} Proposal Request`)}
                            className="px-8 py-3.5 border border-white/20 hover:border-blue-500 rounded-full text-white font-bold transition-all duration-300 backdrop-blur-md bg-white/5 hover:bg-blue-600/10 flex items-center gap-2"
                        >
                            {content.final_cta.secondary} <ArrowRight size={18} />
                        </button>
                    </div>
                </div>
            </section>



        </div>
    );
};

export default LocationPage;
